import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * React hook for fetching and mutating the authenticated buyer's saved
 * searches. Reads through the product-shaped `customerAccount.buying.savedSearches`
 * API; customer identity is resolved server-side from the session cookie.
 *
 * Cosmos uses `graphql-request` via the generated SDK exposed by
 * `getAthenaClient()`, so this hook composes `useState` + `useEffect` instead
 * of Apollo `useQuery`/`useMutation`. After any mutation we refetch the list
 * so consumers don't have to manage cache invalidation themselves.
 */
import { useAuth } from '@opendoor/auth-fe';
import { globalObservability } from '@opendoor/observability/slim';
import type { SavedSearchFieldsFragment, SavedSearchInput } from '__generated__/athena';

import { getAthenaClient } from '../../api';

const Sentry = globalObservability.getSentryClient();

export type SavedSearchMutationResult = {
  success: boolean;
  errorMessage?: string | null;
  savedSearch?: SavedSearchFieldsFragment | null;
};

export type RemoveSavedSearchResult = {
  success: boolean;
  errorMessage?: string | null;
};

export interface UseSavedSearchesApi {
  isLoggedIn: boolean;
  loading: boolean;
  savedSearches: SavedSearchFieldsFragment[];
  add: (input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  update: (id: string, input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  remove: (id: string) => Promise<RemoveSavedSearchResult>;
  refetch: () => Promise<void>;
}

const captureError = (e: unknown, op: string) => {
  Sentry.withScope?.((scope) => {
    scope.setTag('op', op);
    Sentry.captureException?.(e);
  });
};

// The Athena resolver rejects saved searches without any location (market,
// zone, or polygon) with `success: false`. Catch that here so the hook
// surfaces a uniform client-side error without paying for a round-trip.
const inputHasLocation = (input: SavedSearchInput): boolean =>
  !!input.location.market ||
  (input.location.zoneIds?.length ?? 0) > 0 ||
  (input.location.vertices?.length ?? 0) > 0;

const LOCATION_REQUIRED_ERROR = 'A location is required to save a search.';

export function useSavedSearches(): UseSavedSearchesApi {
  const { authentication } = useAuth();
  const isLoggedIn = authentication?.state === 'authenticated';
  const [savedSearches, setSavedSearches] = useState<SavedSearchFieldsFragment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // Track the latest in-flight request so a stale response from a previous
  // auth state (e.g. logout-mid-fetch) cannot overwrite fresher state.
  const requestIdRef = useRef(0);
  // Track the latest auth state so mid-flight mutations don't fire a refetch
  // against an unauthenticated user after logout. Reading from a ref instead
  // of the closure avoids invalidating add/update/remove on every auth change.
  const isLoggedInRef = useRef(isLoggedIn);

  const fetchSavedSearches = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const data = await getAthenaClient().GetSavedSearches();
      if (requestId !== requestIdRef.current) {
        return;
      }
      setSavedSearches(data?.customerAccount?.buying?.savedSearches ?? []);
    } catch (e) {
      captureError(e, 'GetSavedSearches');
      if (requestId !== requestIdRef.current) {
        return;
      }
      setSavedSearches([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
    if (!isLoggedIn) {
      requestIdRef.current++;
      setSavedSearches([]);
      setLoading(false);
      return;
    }
    void fetchSavedSearches();
  }, [isLoggedIn, fetchSavedSearches]);

  const add = useCallback(
    async (input: SavedSearchInput): Promise<SavedSearchMutationResult> => {
      if (!inputHasLocation(input)) {
        return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
      }
      try {
        const res = await getAthenaClient().AddSavedSearch({ input });
        const payload = res?.customerAccount?.buying?.savedSearches?.add;
        if (payload?.success && isLoggedInRef.current) {
          await fetchSavedSearches();
        }
        return {
          success: payload?.success ?? false,
          errorMessage: payload?.errorMessage ?? null,
          savedSearch: payload?.savedSearch ?? null,
        };
      } catch (e) {
        captureError(e, 'AddSavedSearch');
        return { success: false, errorMessage: 'Unexpected error', savedSearch: null };
      }
    },
    [fetchSavedSearches],
  );

  const update = useCallback(
    async (id: string, input: SavedSearchInput): Promise<SavedSearchMutationResult> => {
      if (!inputHasLocation(input)) {
        return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
      }
      try {
        const res = await getAthenaClient().UpdateSavedSearch({ id, input });
        const payload = res?.customerAccount?.buying?.savedSearches?.update;
        if (payload?.success && isLoggedInRef.current) {
          await fetchSavedSearches();
        }
        return {
          success: payload?.success ?? false,
          errorMessage: payload?.errorMessage ?? null,
          savedSearch: payload?.savedSearch ?? null,
        };
      } catch (e) {
        captureError(e, 'UpdateSavedSearch');
        return { success: false, errorMessage: 'Unexpected error', savedSearch: null };
      }
    },
    [fetchSavedSearches],
  );

  const remove = useCallback(
    async (id: string): Promise<RemoveSavedSearchResult> => {
      try {
        const res = await getAthenaClient().RemoveSavedSearch({ id });
        const payload = res?.customerAccount?.buying?.savedSearches?.remove;
        if (payload?.success && isLoggedInRef.current) {
          await fetchSavedSearches();
        }
        return {
          success: payload?.success ?? false,
          errorMessage: payload?.errorMessage ?? null,
        };
      } catch (e) {
        captureError(e, 'RemoveSavedSearch');
        return { success: false, errorMessage: 'Unexpected error' };
      }
    },
    [fetchSavedSearches],
  );

  return {
    isLoggedIn,
    loading,
    savedSearches,
    add,
    update,
    remove,
    refetch: fetchSavedSearches,
  };
}
